import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseDecisionSelectionResult {
  selected: Set<string>
  selectedCount: number
  isSelected: (id: string) => boolean
  toggle: (id: string) => void
  /** Add or remove a batch of ids at once (cluster / select-all). */
  setMany: (ids: string[], checked: boolean) => void
  clear: () => void
  /** True only when every id in `ids` is selected (and `ids` is non-empty). */
  allSelected: (ids: string[]) => boolean
  /** True when some — but not all — of `ids` are selected. */
  someSelected: (ids: string[]) => boolean
}

/**
 * Selection state for the decision queue's multi-select. Keeps a Set of chosen
 * ids and prunes any that fall out of `validIds` (e.g. after an approve/reject
 * refetch removes them) so the batch bar never acts on stale ids.
 */
export function useDecisionSelection(validIds: string[]): UseDecisionSelectionResult {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const validKey = validIds.join(',')

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev
      const valid = new Set(validIds)
      const next = new Set([...prev].filter((id) => valid.has(id)))
      return next.size === prev.size ? prev : next
    })
    // validKey captures the meaningful change; validIds identity churns each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validKey])

  const isSelected = useCallback((id: string) => selected.has(id), [selected])

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const setMany = useCallback((ids: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (checked) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  const allSelected = useCallback(
    (ids: string[]) => ids.length > 0 && ids.every((id) => selected.has(id)),
    [selected],
  )

  const someSelected = useCallback(
    (ids: string[]) => ids.some((id) => selected.has(id)) && !ids.every((id) => selected.has(id)),
    [selected],
  )

  return useMemo(
    () => ({
      selected,
      selectedCount: selected.size,
      isSelected,
      toggle,
      setMany,
      clear,
      allSelected,
      someSelected,
    }),
    [selected, isSelected, toggle, setMany, clear, allSelected, someSelected],
  )
}
